/**
 * Resolves the API base URL from runtime configuration.
 *
 * In production (inside XPLAN), the XService Proxy routes
 * /services/xplan-porter/* to the agent ALB.
 *
 * In local dev, the Vite proxy rewrites /services/xplan-porter to
 * http://localhost:8000.
 */
function getBaseUrl(): string {
  if (globalThis.deployConfig?.porterBffUrl) {
    return globalThis.deployConfig.porterBffUrl;
  }
  return '/services/xplan-porter';
}

export const BFF_BASE_URL = getBaseUrl();
export const MAX_MESSAGE_LENGTH = 500;

/**
 * Persistent assistive-use disclaimer shown in the chat footer on every turn.
 *
 * Single source of truth for the user-facing "this is assistive, verify in
 * XPLAN" affordance. Satisfies the production-readiness controls
 * `ai-labelling-assistive-presentation-and-human-verification` (label responses
 * as assistive + give verification guidance) and
 * `public-responsibilities-documented` (surface user responsibilities / service
 * limits to the adviser). The fuller customer-facing statement lives in
 * `docs/user-responsibilities.md`.
 *
 * Keep this factual and short. It must convey: (1) assistive AI, (2) factual
 * retrieval — not financial advice, (3) verify in XPLAN before relying.
 */
export const ASSISTIVE_DISCLAIMER =
  'Porter is an AI assistant that retrieves factual information from Xplan. ' +
  'It does not provide financial advice. Verify answers in Xplan before relying on them.';

/**
 * Resolves the XPLAN tenant ID.
 *
 * Fetches /resourceful/site (same-origin, cookies auto-attached) and caches
 * the result for the lifetime of the page. Falls back to the Vite dev proxy
 * env var injection (VITE_TENANT_ID) when the endpoint is not available.
 *
 * Returns a promise so callers can await it without blocking module
 * initialisation. Subsequent calls return the same cached promise.
 */
let tenantIdPromise: Promise<string> | null = null;

export function getTenantId(): Promise<string> {
  if (tenantIdPromise) return tenantIdPromise;

  const devFallback = import.meta.env.VITE_TENANT_ID ?? '';

  tenantIdPromise = fetch('/resourceful/site')
    .then((res) => {
      if (!res.ok) return devFallback;
      return res.json().then((data) => data.tenant_id ?? devFallback);
    })
    .catch(() => devFallback);

  return tenantIdPromise;
}

/** Resets the cached promise — for testing only. */
export function _resetTenantIdCache(): void {
  tenantIdPromise = null;
}
